import { useState } from 'react';

import { useVendorProfileApi } from '../../../common/hooks/use-vendor-profile-api.hook';
import { InlineField, inlineFieldForAction } from './insight-presentation';

export interface InlineAnswer {
    /** Null when this finding is not one a single field can answer. */
    field: InlineField | null;
    draft: string;
    setDraft: (value: string) => void;
    save: () => Promise<void>;
    saving: boolean;
    error: boolean;
    canSave: boolean;
}

/**
 * Answering a setup gap where it is asked.
 *
 * Sending a vendor to a settings page to type one website is a detour most
 * will not take, so the field is offered on whatever is asking — a card in
 * the queue, a row in the dashboard widget. Both write the same profile
 * patch, and the server recomputes the gaps, so the thing that asked
 * disappears on its own rather than being crossed off locally.
 */
export function useInlineAnswer(
    /** Anything that asks for one field: a queue finding, a setup step. */
    asked: { id: string; actionKind?: string },
    onResolved?: (id: string) => void,
): InlineAnswer {
    const profileApi = useVendorProfileApi();
    const field = inlineFieldForAction(asked.actionKind);

    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(false);

    const save = async () => {
        if (!field || !draft.trim()) return;
        setSaving(true);
        setError(false);
        try {
            const patch =
                field === 'websiteUrl'
                    ? { websiteUrl: draft.trim() }
                    : { competitors: draft.split(',').map((c) => c.trim()).filter(Boolean) };
            await profileApi.update(patch);
            onResolved?.(asked.id);
        } catch {
            setError(true);
        } finally {
            setSaving(false);
        }
    };

    return { field, draft, setDraft, save, saving, error, canSave: Boolean(draft.trim()) && !saving };
}
